import React, { useRef, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

function RingedStars() {
  const starsRef = useRef();
  const particlesRef = useRef([]);
  const { camera } = useThree();
  const goldenRatio = 1.618;

  const ringedStars = useMemo(() => {
    const stars = [];

    for (let i = 0; i < 3; i++) {
      let position;
      let safe = false;

      while (!safe) {
        position = new THREE.Vector3(
          (Math.random() - 0.5) * 1500,
          (Math.random() - 0.5) * 1500,
          (Math.random() - 0.5) * 1500
        );

        safe = !stars.some(star => position.distanceTo(star.position) < 100);
      }

      stars.push({
        position,
        size: Math.random() * 2 + 2,
        color: new THREE.Color(`hsl(${Math.random() * 360}, 100%, 80%)`),
        ringTilt: new THREE.Euler(
          Math.random() * Math.PI,
          Math.random() * Math.PI,
          Math.random() * Math.PI
        ),
        particles: Array.from({ length: 150 }).map(() => ({
          angle: Math.random() * Math.PI * 2,
          distance: Math.random() * 2 + 2,
          speed: Math.random() * 0.001 + 0.0005,
          zOffset: (Math.random() - 0.5) * 0.2
        }))
      });
    }

    // 🌟 **První hvězda – zlatý řez**
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    const distance = 120;
    const centerPos = camera.position.clone().add(direction.multiplyScalar(distance));

    stars[0].position.set(
      centerPos.x + distance / goldenRatio,
      centerPos.y - distance / goldenRatio,
      centerPos.z
    );

    // ⭐ **Druhá hvězda – malá variace kolem středu**
    stars[1].position.set(
      centerPos.x - 50,
      centerPos.y + 30,
      centerPos.z - 50
    );

    return stars;
  }, [camera]);

  useFrame(() => {
    if (starsRef.current) {
      starsRef.current.rotation.y += 0.00005;
    }

    particlesRef.current.forEach((particle, i) => {
      if (particle) {
        const parentStar = ringedStars[Math.floor(i / 300)];
        const particleData = parentStar.particles[i % 300];

        particleData.angle += particleData.speed;

        const radius = parentStar.size * (2 + (i % 3) * 0.7);
        particle.position.set(
          Math.cos(particleData.angle) * radius,
          Math.sin(particleData.angle) * radius,
          particleData.zOffset
        );
      }
    });
  });

  return (
    <group ref={starsRef}>
      {ringedStars.map((star, i) => (
        <group key={i} position={star.position}>
          <mesh>
            <sphereGeometry args={[star.size, 32, 32]} />
            <meshStandardMaterial color={star.color} emissive={star.color} emissiveIntensity={3} />
          </mesh>

          {/* 🌀 Víceúrovňový prstenec */}
          <group rotation={star.ringTilt}>
            {star.particles.map((particle, j) => (
              <mesh key={j} ref={(el) => (particlesRef.current[i * 300 + j] = el)}>
                <sphereGeometry args={[Math.random() * 0.3 + 0.1, 8, 8]} />
                <meshStandardMaterial
                  color={star.color}
                  emissive={star.color}
                  emissiveIntensity={Math.random() * 1.2 + 0.3}
                />
              </mesh>
            ))}
          </group>
        </group>
      ))}
    </group>
  );
}

export default RingedStars;